/**
 * Scans Store
 * CD-286: My Scans list page
 * CD-287: Scan status view
 * CD-288: Scan initiation UI
 *
 * Manages scan state and operations
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import scanService from '@/services/scanService'
import { useBillingStore } from '@/stores/billing'

// CD-566 round 2 (2026-05-21): canonical key for matching scan rows
// across REST list_scans and SSE realtime events. Both backend paths
// emit `request_id` derived as:
//   CASE WHEN cdbe_scan_id IS NOT NULL THEN cdbe_scan_id
//        ELSE CAST(scan_queue.id AS VARCHAR) END
// so request_id is THE stable identity. Numeric id can change types
// (int from SSE vs string from REST) and scan_id is a frontend-only
// alias. Always coerce to string to avoid 30 !== "30" mismatches.
function _canonicalKey(scan) {
  if (!scan) return null
  // Prefer request_id (string from REST, string from SSE r2+).
  // Fall back to cdbe_scan_id if present, then stringified queue id.
  if (scan.request_id != null) return String(scan.request_id)
  if (scan.cdbe_scan_id) return String(scan.cdbe_scan_id)
  if (scan.scan_id != null) return String(scan.scan_id)
  if (scan.id != null) return String(scan.id)
  return null
}

// CD-574 (2026-05-22): canonical timestamp for sort. REST emits
// `initiated_at` (aliased from sq.queued_at), SSE may emit
// `created_at` or `queued_at`. Returns 0 when none parseable so
// malformed rows sink to the bottom rather than throwing NaN
// comparisons.
function _initiatedTs(scan) {
  if (!scan) return 0
  const raw = scan.initiated_at || scan.created_at || scan.queued_at
  if (!raw) return 0
  const t = new Date(raw).getTime()
  return Number.isFinite(t) ? t : 0
}

export const useScansStore = defineStore('scans', () => {
  // State
  const scans = ref([])
  const currentScan = ref(null)
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })
  const loading = ref(false)
  const error = ref(null)
  const filter = ref({
    status: null
  })

  // Active scans being polled
  const pollingScans = ref(new Map())

  // Computed
  const runningScans = computed(() =>
    scans.value.filter(s => ['queued', 'pending', 'running', 'processing', 'in_progress'].includes(s.status))
  )

  const completedScans = computed(() =>
    scans.value.filter(s => s.status === 'completed')
  )

  const hasScans = computed(() => scans.value.length > 0)

  /**
   * Fetch scans list with pagination
   *
   * CD-566 round 2: when the SSE channel has already seeded rows
   * (e.g. a freshly-initiated scan delivered via scan_added before
   * the REST list reflected it), `fetchScans()` used to overwrite
   * `scans.value` outright and wipe those rows. We now merge by
   * canonical key — REST results win on fields they carry, but any
   * SSE-only rows that REST hasn't picked up yet are preserved
   * at the top of the list until the next REST page boundary
   * naturally reconciles them.
   *
   * @param {Object} options - Fetch options
   */
  async function fetchScans(options = {}) {
    loading.value = true
    error.value = null

    try {
      const params = {
        page: options.page || pagination.value.page,
        limit: options.limit || pagination.value.limit,
        ...filter.value
      }

      // Remove null/undefined params
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      const response = await scanService.listScans(params)
      // Normalize REST scan rows. Both `scan_id` and `id` are coerced
      // to strings so they line up with SSE-emitted request_id values.
      const restRows = (response.scans || []).map(scan => {
        const key = _canonicalKey(scan)
        return {
          ...scan,
          request_id: scan.request_id || scan.scan_id || (scan.id != null ? String(scan.id) : null),
          scan_id: scan.scan_id || scan.request_id || (scan.id != null ? String(scan.id) : null),
          id: scan.id != null ? scan.id : (scan.scan_id || scan.request_id),
          _canonical_key: key,
        }
      })

      // Preserve SSE-only rows. After the first page on page 1 the
      // REST result is authoritative for what should be visible. For
      // pages > 1 we just overwrite (pagination scenario).
      if ((options.page || pagination.value.page) === 1) {
        const restKeys = new Set(restRows.map(r => r._canonical_key).filter(Boolean))
        const sseOnly = scans.value.filter(s => {
          const k = _canonicalKey(s)
          return k && !restKeys.has(k)
        })
        // CD-574 (2026-05-22): sort the merged array by initiated_at
        // DESC. Naive concat of [...sseOnly, ...restRows] put SSE-only
        // rows on top regardless of timestamp, so a stale SSE row
        // surviving on the store (KeepAlive caches this view) would
        // appear above newer REST rows. _initiatedTs() falls back
        // through initiated_at → created_at → queued_at so both REST
        // and SSE row shapes sort consistently.
        scans.value = [...sseOnly, ...restRows].sort(
          (a, b) => _initiatedTs(b) - _initiatedTs(a)
        )
      } else {
        // Pagination scenario: still sort, REST may not be perfectly
        // DESC under all DB-side caching conditions and the frontend
        // owns the visible ordering contract per CD-574.
        scans.value = restRows.slice().sort(
          (a, b) => _initiatedTs(b) - _initiatedTs(a)
        )
      }

      // Handle both nested pagination object and flat response
      pagination.value = response.pagination || {
        page: response.page || 1,
        limit: response.limit || 20,
        total: response.total || 0,
        pages: response.pages || 0
      }
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to fetch scans'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch single scan by ID
   * @param {string} scanId
   */
  async function fetchScan(scanId) {
    loading.value = true
    error.value = null

    try {
      const scan = await scanService.getScan(scanId)
      currentScan.value = scan
      return scan
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to fetch scan'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get scan status (for polling active scans)
   * @param {string} scanId
   */
  async function getScanStatus(scanId) {
    try {
      const status = await scanService.getScanStatus(scanId)

      // Update in list
      const index = scans.value.findIndex(s => s.id === scanId || s.scan_id === scanId)
      if (index !== -1) {
        scans.value[index] = { ...scans.value[index], ...status }
      }

      // Update current if viewing
      if (currentScan.value?.id === scanId || currentScan.value?.scan_id === scanId) {
        currentScan.value = { ...currentScan.value, ...status }
      }

      return status
    } catch (err) {
      throw err
    }
  }

  /**
   * Initiate new scan
   * @param {Object} data - Scan data
   */
  async function initiateScan(data) {
    loading.value = true
    error.value = null

    try {
      const scan = await scanService.initiateScan(data)
      // Add to list if on first page
      if (pagination.value.page === 1) {
        scans.value.unshift(scan)
        pagination.value.total += 1
      }

      // CD-223: Refresh balance after scan initiation (1 credit deducted)
      try {
        const billingStore = useBillingStore()
        await billingStore.fetchBalance()
      } catch (balanceErr) {
        // Silently ignore balance refresh errors
        console.warn('Failed to refresh balance:', balanceErr)
      }

      return scan
    } catch (err) {
      // CD-460: Provide specific error message for timeout errors
      if (err.code === 'TIMEOUT') {
        error.value = 'Scan request timed out. The server may still be processing it. Please check your scan history.'
      } else {
        error.value = err.detail || err.message || 'Failed to initiate scan'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Cancel scan
   * @param {string} scanId
   */
  async function cancelScan(scanId) {
    loading.value = true
    error.value = null

    try {
      const result = await scanService.cancelScan(scanId)

      // Update in list
      const index = scans.value.findIndex(s => s.id === scanId || s.scan_id === scanId)
      if (index !== -1) {
        scans.value[index].status = 'cancelled'
      }

      // Update current if viewing
      if (currentScan.value?.id === scanId || currentScan.value?.scan_id === scanId) {
        currentScan.value.status = 'cancelled'
      }

      // Stop polling if active
      stopPolling(scanId)

      // CD-502: Refresh header balance — if the cancel was early enough the
      // backend refunds the 1 credit, and the user should see it immediately.
      try {
        const billingStore = useBillingStore()
        await billingStore.fetchBalance()
      } catch (balanceErr) {
        console.warn('Failed to refresh balance after scan cancel:', balanceErr)
      }

      return result
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to cancel scan'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get report URL
   * @param {string} scanId
   * @param {string} format
   */
  async function getReport(scanId, format = 'html') {
    try {
      return await scanService.getReport(scanId, format)
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to get report'
      throw err
    }
  }

  /**
   * Get export URL
   * @param {string} scanId
   * @param {string} format
   */
  async function exportData(scanId, format = 'json') {
    try {
      return await scanService.exportData(scanId, format)
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to export data'
      throw err
    }
  }

  /**
   * Start polling scan status
   * @param {string} scanId
   * @param {Function} onUpdate - Callback on status update
   * @param {number} interval - Polling interval in ms
   */
  function startPolling(scanId, onUpdate, interval = 3000) {
    if (pollingScans.value.has(scanId)) return

    const poll = async () => {
      try {
        const status = await getScanStatus(scanId)
        if (onUpdate) onUpdate(status)

        // Stop polling if scan is complete
        if (['completed', 'failed', 'cancelled'].includes(status.status)) {
          stopPolling(scanId)
        }
      } catch (err) {
        console.error(`Polling error for scan ${scanId}:`, err)
      }
    }

    // Initial poll
    poll()

    // Set interval
    const intervalId = setInterval(poll, interval)
    pollingScans.value.set(scanId, intervalId)
  }

  /**
   * Stop polling scan status
   * @param {string} scanId
   */
  function stopPolling(scanId) {
    const intervalId = pollingScans.value.get(scanId)
    if (intervalId) {
      clearInterval(intervalId)
      pollingScans.value.delete(scanId)
    }
  }

  /**
   * Stop all polling
   */
  function stopAllPolling() {
    pollingScans.value.forEach((intervalId, scanId) => {
      clearInterval(intervalId)
    })
    pollingScans.value.clear()
  }

  /**
   * Set filter and refresh
   * @param {Object} newFilter
   */
  async function setFilter(newFilter) {
    filter.value = { ...filter.value, ...newFilter }
    pagination.value.page = 1
    return fetchScans()
  }

  /**
   * Go to page
   * @param {number} page
   */
  async function goToPage(page) {
    pagination.value.page = page
    return fetchScans()
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  /**
   * CD-566: upsert a scan from a realtime SSE event.
   *
   * Round 2 (2026-05-21): matches by `request_id` (the stable
   * cross-source key) coerced to string, falling back to
   * cdbe_scan_id or stringified queue id. The previous strict-=== id
   * comparison missed because SSE sends integer `id` while REST
   * returns stringified id, leading to duplicate prepends.
   *
   * Merge policy: REST/existing row fields are preserved when the
   * SSE row would clobber them with a less-specific value
   * (e.g. SSE sending `scan_id: "30"` when REST already had
   * `scan_id: "<uuid>"`).
   */
  function upsertScanFromRealtime(scan) {
    if (!scan) return
    const key = _canonicalKey(scan)
    if (!key) return

    // CD-598: also match an existing row by the stable queue id, not only the
    // canonical key. The backend derives request_id as cdbe_scan_id-or-queue_id
    // (realtime.py), so a scan's canonical key FLIPS from the queue id to the
    // CDBE UUID when it is released to CDBE — which made the queued→processing
    // update miss the existing row and append a DUPLICATE. The queue id (`id`)
    // is stable across the whole lifecycle, so matching on it too keeps one row.
    const queueId = scan.id != null ? String(scan.id) : null
    const idx = scans.value.findIndex(
      s =>
        _canonicalKey(s) === key ||
        (queueId !== null && s.id != null && String(s.id) === queueId)
    )
    // Normalize SSE row to the FE row shape (string ids).
    const normalized = {
      ...scan,
      request_id: scan.request_id || (scan.cdbe_scan_id ? String(scan.cdbe_scan_id) : (scan.id != null ? String(scan.id) : null)),
      scan_id: scan.scan_id || scan.cdbe_scan_id || (scan.id != null ? String(scan.id) : null),
      id: scan.id != null ? scan.id : (scan.scan_id || scan.request_id),
      _canonical_key: key,
    }
    if (idx >= 0) {
      const existing = scans.value[idx]
      // Don't downgrade a UUID scan_id back to a stringified queue id.
      // If existing.scan_id looks like a UUID-ish (long, has dashes)
      // and normalized.scan_id is a short numeric string, prefer existing.
      const existingScanIdLooksUuid = typeof existing.scan_id === 'string'
        && existing.scan_id.length > 10
        && existing.scan_id.includes('-')
      const newScanIdLooksQueueId = typeof normalized.scan_id === 'string'
        && /^\d+$/.test(normalized.scan_id)
      if (existingScanIdLooksUuid && newScanIdLooksQueueId) {
        normalized.scan_id = existing.scan_id
      }
      scans.value[idx] = { ...existing, ...normalized }
    } else {
      // CD-574 (2026-05-22): insert at the position dictated by
      // initiated_at DESC instead of unconditional unshift. Keeps the
      // visible order monotonically newest-first even if SSE delivers
      // an out-of-order event (clock skew, late delivery, etc.).
      const ts = _initiatedTs(normalized)
      const insertAt = scans.value.findIndex(s => _initiatedTs(s) < ts)
      if (insertAt === -1) {
        scans.value.push(normalized)
      } else {
        scans.value.splice(insertAt, 0, normalized)
      }
    }
  }

  /**
   * Reset store
   */
  function reset() {
    stopAllPolling()
    scans.value = []
    currentScan.value = null
    pagination.value = { page: 1, limit: 20, total: 0, pages: 0 }
    loading.value = false
    error.value = null
    filter.value = { status: null }
  }

  return {
    // State
    scans,
    currentScan,
    pagination,
    loading,
    error,
    filter,

    // Computed
    runningScans,
    completedScans,
    hasScans,

    // Actions
    fetchScans,
    fetchScan,
    getScanStatus,
    initiateScan,
    cancelScan,
    getReport,
    exportData,
    startPolling,
    stopPolling,
    stopAllPolling,
    setFilter,
    goToPage,
    clearError,
    reset,

    // CD-566: realtime update entry-point
    upsertScanFromRealtime,
  }
})
